import { useState, useEffect, useContext, createContext, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Sidebar from '../../components/navigation/Sidebar';
import { PortalViewProvider } from '../../components/navigation/PortalView';
import ChatPanel from '../../components/chat/ChatPanel';
import {
  ListTodo,
  Users as UsersIcon,
  Building,
  Folders,
  SquareKanban,
  HousePlus,
  UserPlus,
  Zap,
  Briefcase,
  Search as SearchIcon,
  Binoculars,
  FileSearch,
  Building2,
  AlertTriangle,
  ClipboardList
} from 'lucide-react';
import aiTablesIcon from '../../assets/aitables.png';
import { supabase } from '../../utilities/supabase';
import { Alert, Badge, LogoLoader, useNotification } from '@core/components';
import { UserProfileContext } from '../../App';
import { EntityTypeEnum } from '../../utilities/Enumerables';
import { canSeeDashboard, canView } from '../../utilities/Permissions';
import lazyWithReload from '../../utilities/lazyWithReload';

// Pages are code-split: each loads when its route is first opened.
const Home = lazyWithReload(() => import('./home/Index'));
const V2Home = lazyWithReload(() => import('./home/v2/V2Home'));
const Users = lazyWithReload(() => import('./users/Users'));
const UserDetails = lazyWithReload(() => import('./users/User'));
const Documents = lazyWithReload(() => import('./documents/Documents'));
const Tasks = lazyWithReload(() => import('./tasks/Tasks'));
const Pipelines = lazyWithReload(() => import('./pipelines/Pipelines'));
const Automations = lazyWithReload(() => import('./automations/Automations'));
const Settings = lazyWithReload(() => import('./settings/Settings'));
const Account = lazyWithReload(() => import('../account/Account'));
const Help = lazyWithReload(() => import('./help/Help'));
const Docs = lazyWithReload(() => import('./help/docs/Docs'));
const Doc = lazyWithReload(() => import('./help/docs/Doc'));
const Category = lazyWithReload(() => import('./help/docs/Category'));
const CreateDoc = lazyWithReload(() => import('./help/docs/CreateDoc'));
const Resources = lazyWithReload(() => import('./help/resources/Resources'));
const View = lazyWithReload(() => import('./help/resources/View'));
const Companies = lazyWithReload(() => import('./companies/Companies'));
const Company = lazyWithReload(() => import('./companies/Company'));
const CreateCompany = lazyWithReload(() => import('./companies/CreateCompany'));
const Pensions = lazyWithReload(() => import('./pensions/Pensions'));
const PensionDetails = lazyWithReload(() => import('./pensions/Pension'));
const FactFinds = lazyWithReload(() => import('./factfinds/FactFinds'));
const Traces = lazyWithReload(() => import('./traces/Traces'));
const TraceDetails = lazyWithReload(() => import('./traces/Trace'));
const Searches = lazyWithReload(() => import('./searches/Searches'));
const SearchDetails = lazyWithReload(() => import('./searches/Search'));
const Leads = lazyWithReload(() => import('./leads/Leads'));
const LeadDetails = lazyWithReload(() => import('./leads/Lead'));
const AiTables = lazyWithReload(() => import('./aiTables/AiTables'));
const AiTable = lazyWithReload(() => import('./aiTables/AiTable'));
const Providers = lazyWithReload(() => import('./providers/Providers'));
const ProviderDetails = lazyWithReload(() => import('./providers/Provider'));
const Schemes = lazyWithReload(() => import('./schemes/Schemes'));
const SchemeDetails = lazyWithReload(() => import('./schemes/Scheme'));
const CreateProvider = lazyWithReload(() => import('./providers/CreateProvider'));
const CreateUser = lazyWithReload(() => import('./users/CreateUser'));
const CreateScheme = lazyWithReload(() => import('./schemes/CreateScheme'));

// /demo: the new screens on sample data (loaded only when opened)
const Demos = lazyWithReload(() => import('../demo/Demos'));

export const EntityHistoryContext = createContext([]);

const Dashboard = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showDemoMessage, setShowDemoMessage] = useState(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const { addNotification } = useNotification();
  const { userProfile } = useContext(UserProfileContext);
  const [entityHistory, setEntityHistory] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const channel = supabase
      .channel('notifications')
      .on('broadcast', { event: 'notification' }, ({ payload }) => {
        if (
          payload.recipientId == userProfile?.id ||
          payload.broadcast === true
        ) {
          addNotification({
            title: payload.title,
            description: payload.message,
            type: payload.type || 'info',
            duration: payload.duration || 5000
          });
        }
      })
      .subscribe();

    const githubUpdates = supabase
      .channel('github-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'github_updates'
        },
        () => {
          setShowUpdateBanner(true);
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
      githubUpdates.unsubscribe();
    };
  }, [userProfile, addNotification]);

  useEffect(() => {
    // Load dark mode preference from local storage
    const userPreferences =
      JSON.parse(localStorage.getItem('user_preferences')) || {};
    setIsDarkMode(userPreferences.darkMode || false);
  }, []);

  useEffect(() => {
    // Extract the entity ID from the path (e.g., /user-profiles/205025/searches becomes 205025)
    const pathParts = location.pathname.split('/');
    const entityId = pathParts[2]; // Get the entity ID

    let basePath = `/${pathParts[1]}`;
    if (entityId) {
      // Construct the base path using the entity type and ID
      basePath = `/${pathParts[1]}/${entityId}`;
    }

    setEntityHistory(prevHistory => {
      // Check if the base path is already in the history
      if (prevHistory.includes(basePath)) {
        return prevHistory; // Don't add duplicates
      }

      const newHistory = [...prevHistory, basePath];
      return newHistory;
    });
  }, [location.pathname, setEntityHistory]);

  const sidebarItems = [
    {
      icon: SquareKanban,
      name: 'Pipelines',
      path: '/pipelines',
      entityType: EntityTypeEnum.Pipelines
    },
    {
      icon: ListTodo,
      name: 'Tasks',
      path: '/tasks',
      entityType: EntityTypeEnum.Task
    },
    {
      icon: UserPlus,
      name: 'Leads',
      path: '/leads',
      entityType: EntityTypeEnum.Lead
    },
    {
      icon: UsersIcon,
      name: 'Users',
      path: '/user-profiles',
      entityType: EntityTypeEnum.User
    },
    {
      icon: Binoculars,
      name: 'Traces',
      path: '/traces',
      entityType: EntityTypeEnum.Trace
    },
    {
      icon: SearchIcon,
      name: 'Searches',
      path: '/searches',
      entityType: EntityTypeEnum.Search
    },
    {
      icon: Briefcase,
      name: 'Pensions',
      path: '/pensions',
      entityType: EntityTypeEnum.Pension
    },
    {
      icon: ClipboardList,
      name: 'Fact Finds',
      path: '/fact-finds',
      entityType: EntityTypeEnum.User
    },
    {
      icon: Building,
      name: 'Companies',
      path: '/companies',
      entityType: EntityTypeEnum.Company
    },
    {
      icon: Building2,
      name: 'Providers',
      path: '/providers',
      entityType: EntityTypeEnum.Provider
    },
    {
      icon: FileSearch,
      name: 'Schemes',
      path: '/schemes',
      entityType: EntityTypeEnum.Scheme
    },
    {
      icon: Folders,
      name: 'Documents',
      path: '/documents',
      entityType: EntityTypeEnum.Document
    },
    {
      icon: Zap,
      name: 'Automations',
      path: '/automations',
      entityType: EntityTypeEnum.Automations
    },
    {
      icon: ({ size, className }) => (
        <img
          src={aiTablesIcon}
          alt='+Ai Tables'
          width={size}
          height={size}
          className={`${className} rounded-sm`}
        />
      ),
      name: '+Ai Tables',
      path: '/ai-tables',
      entityType: EntityTypeEnum.AiTableView
    }
  ];

  // ROLE_PAGES_V1: the dashboard and +Ai Tables follow the role's permissions. A role without
  // the dashboard lands on its first sidebar page instead.
  const firstPage = sidebarItems.find(i => canView(userProfile, i.entityType))?.path;
  const denied = what => (
    <Alert title='Permission Denied' description={`You do not have permission to view ${what}.`} style='danger' />
  );
  const dashboard = page =>
    canSeeDashboard(userProfile) ? page : firstPage ? <Navigate to={firstPage} replace /> : denied('the dashboard');
  const aiTables = page => (canView(userProfile, EntityTypeEnum.AiTableView) ? page : denied('+Ai Tables'));

  return (
    <EntityHistoryContext.Provider value={{ entityHistory, setEntityHistory }}>
      <PortalViewProvider>
      <div
        className={`flex flex-col md:flex-row h-screen w-screen ${
          isDarkMode ? 'dark' : ''
        }`}
      >
        {showUpdateBanner && (
          <div className='fixed top-0 left-0 right-0 bg-green-500 text-white p-4 flex justify-between items-center z-50'>
            <span>A new version is available. Please refresh to update.</span>
            <button
              onClick={() => window.location.reload()}
              className='bg-white text-red-500 px-4 py-2 rounded hover:bg-red-100'
            >
              Refresh
            </button>
          </div>
        )}
        <div className='hidden lg:block' data-cursor-anchor='sidebar'>
          <Sidebar items={sidebarItems} />
        </div>
        <div className='flex flex-col flex-1 bg-white dark:bg-neutral-900 w-full overflow-hidden'>
          <Header
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
            sidebarItems={sidebarItems}
          />
          <div className='flex flex-row flex-1 min-h-0 overflow-hidden'>
            <main data-cursor-anchor='main' className='flex-auto text-neutral-600 dark:text-neutral-300 overflow-auto'>
              <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <Routes>
              <Route path='/' element={dashboard(<Home />)} />
              <Route path='/v2' element={dashboard(<V2Home />)} />
              <Route path='/user-profiles' element={<Users />} />
              <Route path='/user-profiles/:id/*' element={<UserDetails />} />
              <Route path='/user-profiles/create' element={<CreateUser />} />
              <Route path='/companies' element={<Companies />} />
              <Route path='/companies/create' element={<CreateCompany />} />
              <Route path='/companies/:id/*' element={<Company />} />
              <Route path='/pensions' element={<Pensions />} />
              <Route path='/pensions/:id/*' element={<PensionDetails />} />
              <Route path='/fact-finds' element={<FactFinds />} />
              <Route path='/traces' element={<Traces />} />
              <Route path='/traces/:id/*' element={<TraceDetails />} />
              <Route path='/searches' element={<Searches />} />
              <Route path='/searches/:id/*' element={<SearchDetails />} />
              <Route path='/documents' element={<Documents />} />
              <Route path='/tasks/*' element={<Tasks />} />
              <Route path='/pipelines/:id?' element={<Pipelines />} />
              <Route path='/pipelines/:id/*' element={<Pipelines />} />
              <Route
                path='/pipelines/:id/item/:itemId/*'
                element={<Pipelines />}
              />
              <Route path='/settings/*' element={<Settings />} />
              <Route path='/account/*' element={<Account />} />
              <Route path='/automations' element={<Automations />} />
              <Route
                path='/automations/create'
                element={<Automations create={true} />}
              />
              <Route path='/help' element={<Help />} />
              <Route path='/help/docs' element={<Docs />} />
              <Route path='/help/docs/create' element={<CreateDoc />} />
              <Route path='/help/docs/:category' element={<Category />} />
              <Route path='/help/docs/:category/:slug' element={<Doc />} />
              <Route path='/help/resources' element={<Resources />} />
              <Route path='/help/resources/:id' element={<View />} />
              <Route path='/providers' element={<Providers />} />
              <Route path='/providers/:id/*' element={<ProviderDetails />} />
              <Route path='/providers/create' element={<CreateProvider />} />
              <Route
                path='/demo/*'
                element={
                  <Suspense fallback={null}>
                    <Demos />
                  </Suspense>
                }
              />
              <Route path='/schemes' element={<Schemes />} />
              <Route path='/schemes/:id/*' element={<SchemeDetails />} />
              <Route path='/schemes/create' element={<CreateScheme />} />
              <Route path='/leads' element={<Leads />} />
              <Route path='/leads/:id/*' element={<LeadDetails />} />
              <Route path='/ai-tables' element={aiTables(<AiTables />)} />
              <Route path='/ai-tables/:id' element={aiTables(<AiTable />)} />
              </Routes>
              </Suspense>
            </main>
            <ChatPanel />
          </div>
        </div>
      </div>
      </PortalViewProvider>
    </EntityHistoryContext.Provider>
  );
};

export default Dashboard;
